import React, { useState, useContext } from "react";
import { Box, Button, makeStyles, RadioGroup } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { useTranslation } from "react-i18next";
import { Input, Textarea, UserContext } from "@micado-digital/react-intown-core";
import Item from "./item";

const useStyles = makeStyles((theme) => ({
	radioRoot: {
		display: "grid",
		gap: theme.spacing(1.5)
	}
}));

const PillowMenu = () => {
	const css = useStyles();
	const { i18n } = useTranslation();
	const [value, setValue] = useState(localStorage.getItem("pillowReservation"));
	const [formError, setFormError] = useState(false);
	const [userData] = useContext(UserContext);

	if (!userData) {
		return <></>;
	}

	const { lastname, roomNumber } = userData;

	const handleChange = (event) => {
		setValue(event.target.value);
	};

	const handleSubmit = (event) => {
		event.preventDefault();

		const { REACT_APP_PATH, REACT_APP_PILLOWMENU_RECEIVER } = process.env;

		const formData = new FormData(event.target);
		formData.append("layout", "mail");
		formData.append("lang", i18n.language);
		formData.append("receiver", REACT_APP_PILLOWMENU_RECEIVER);
		formData.append("subject", "Polstermenü");
		formData.append("redir", "/");
		formData.append("fields", "lastname=Nachname;roomnumber=Zimmernummer;notes=Anmerkungen;pillow=Polster");

		fetch(REACT_APP_PATH + "/sendmail.api", {
			body: formData,
			method: "POST"
		}).then((response) => {
			if (response.status === 200) {
				localStorage.setItem("pillowReservation", formData.get("pillow"));
				window.location.href = `/${i18n.language}/pillow-success.html`;
			} else {
				setFormError(true);
			}
		});
	};

	const pillowList = [
		{
			de: {
				label: "Seitenschläferkissen",
				addition: "Optimaler Schutzkomfort beim Schlafen",
				text: "Format: 35x160 cm, bietet optimalen Stützkomfort in seitlichen Schlafpositionen"
			},
			en: {
				label: "Side-sleeper pillow",
				addition: "Optimal protective comfort when sleeping",
				text: "Format: 35x160 cm, provides optimal and comfortable support in side-sleeping positions"
			}
		},
		{
			de: {
				label: "Softbausch Standard",
				addition: "Für den kuscheligen Entspannungsschlaf",
				text: "Angenehmes Frischegefühl dank ausgezeichneter Luftzirkulation und höchster Bauschkraft"
			},
			en: {
				label: "Softbausch Standard",
				addition: "For that cuddly, relaxing sleep",
				text: "Pleasant feeling of freshness thanks to excellent air circulation and maximum bulking power"
			}
		},
		{
			de: {
				label: "Wellness Allergic",
				addition: "Das Wohlfühlpolster für Allergiker",
				text: "Dauerhafter Aktivschutz vor Hausstaubmilben, Bakterien und Pilzen"
			},
			en: {
				label: "Wellness Allergic",
				addition: "The feel-good pillow for allergy sufferers",
				text: "Permanent active protection against house dust mites, bacteria and fungus"
			}
		},
		{
			de: {
				label: "Wellness Beauty",
				addition: "Für einen vitalisierenden Schönheitsschlaf",
				text: "100% reines Naturprodukt mit der hautpflegenden Kraft von Meeresalgen"
			},
			en: {
				label: "Wellness Beauty",
				addition: "For vitalising beauty sleep",
				text: "100% purely natural product with the skin nourishing power of seaweed"
			}
		},
		{
			de: {
				label: "Klimacontrol Comfort",
				addition: "Das klimaregulierende Polster",
				text: "Für ein unvergleichliches Trockenschlafklima. Einzigartiger Feuchtigkeitstransport dank HEFEL-TENCEL®"
			},
			en: {
				label: "Climatecontrol Comfort",
				addition: "The temperatureregulating pillow",
				text: "For an unparalleled dry sleeping climate. Unique moisture transport thanks to HEFEL-TENCEL®"
			}
		},
		{
			de: {
				label: "Mediflex Optima Nackenstützkissen",
				addition: "Das Nackenstützkissen für den Erholungsschlaf",
				text: "Mit optimaler Stützkraft für die Halswirbelsäule dank einzigartigem Baukissensystem"
			},
			en: {
				label: "Mediflex Optima Neck support pillow",
				addition: "The neck support pillow for relaxing sleep",
				text: "With optimal support for the cervical spine thanks to a unique modular pillow system"
			}
		},
		{
			de: {
				label: "HEFEL Zirbenbettwarenset",
				addition: "Zirbe tut dem Kreislauf und dem Herzen gut",
				text: "Das hochalpine Holz reduziert die Herzfrequenz und erhöht so die Schlafqualität. Bestehend aus Bettdecke, Polster und Unterbett"
			},
			en: {
				label: "HEFEL Swiss Stone pine bedding set",
				addition: "Swiss Stone pine is beneficial for the cardiovascular system",
				text: "The high alpine wood reduces the heart rate and thus boosts the quality of sleep.Consists of comforter, pillow and mattress topper"
			}
		}
	];

	return (
		<form method="GET" onSubmit={handleSubmit}>
			<RadioGroup className={css.radioRoot} aria-label="pillow" name="pillow" value={value} onChange={handleChange}>
				{pillowList.map((item, counter) => {
					return (
						<Item
							key={counter}
							label={item[i18n.language]?.label}
							addition={item[i18n.language]?.addition}
							text={item[i18n.language]?.text}
							current={value === item[i18n.language]?.label ? true : false}
						></Item>
					);
				})}
			</RadioGroup>
			<Box mt={3}>
				<Input
					name="lastname"
					label={i18n.language === "de" ? "Nachname" : "Lastname"}
					required
					defaultValue={lastname}
					InputProps={{
						readOnly: true
					}}
				/>
			</Box>
			<Box mt={3}>
				<Input
					name="roomnumber"
					label={i18n.language === "de" ? "Zimmernummer" : "Room number"}
					defaultValue={roomNumber}
					InputProps={{
						readOnly: true
					}}
					required
				/>
			</Box>
			<Box mt={3}>
				<Textarea name="notes" label={i18n.language === "de" ? "Anmerkungen" : "Notes"} />
			</Box>
			{formError && (
				<Box mt={3}>
					<Alert severity="error">
						{i18n.language === "de"
							? "Leider konnte die Reservierung nicht übermittelt werden, bitte melden Sie Ihren Polster-Wunsch direkt an der Rezeption."
							: "Unfortunately the reservation could not be transmitted, please report your request directly to reception."}
					</Alert>
				</Box>
			)}
			<Box mt={3}>
				<Button color="secondary" variant="contained" type="submit">
					{i18n.language === "de" ? "Polster reservieren" : "Reserve pillow"}
				</Button>
			</Box>
		</form>
	);
};

export default PillowMenu;
